/** External */
import _ from "lodash";
import { useI18n } from "Hooks";

import classNames from "classnames";
import React, { useMemo, useState } from "react";
import { elevations, NoSearchInFilterIllustration } from "@toddle-edu/ds-theme";
import { Popover } from "@toddle-edu/react-tiny-popover";
import { Checkbox, EmptyState, SearchInput } from "@toddle-edu/ds-web";

/** Internal */
import Accordian from "UIComponents/Accordian";
import { colors } from "Constants";

/** Relative */
import classes from "./AssessmentLearningGoalsSearchInput.scss";
import { getSearchedLearningGoals } from "../../../../module/AssessmentLearningGoalsUtils";

const WithTextHighlight = ({ text, highlight, className = "" }) => {
  const parts = _.split(
    text,
    new RegExp(`(${_.escapeRegExp(highlight)})`, "gi")
  );
  return (
    <span className={className}>
      {" "}
      {_.map(parts, (part, i) => (
        <span
          key={i}
          style={
            part.toLowerCase() === highlight.toLowerCase()
              ? { color: colors.blue400, fontWeight: "600" }
              : {}
          }
        >
          {part}
        </span>
      ))}{" "}
    </span>
  );
};

const CheckboxList = ({
  options,
  onToggle,
  isChecked,
  searchText,
  hasNestedChildren,
}) => {
  const { depth = 1 } = _.first(options) || {};

  const _hasNestedChildren =
    depth === 1 &&
    _.some(options, option =>
      _.some(
        option?.childNodes,
        node => !_.isEmpty(_.get(node, "childNodes", []))
      )
    );

  return (
    <div className={classes.checkListMainContainer}>
      {_.map(options, item => {
        const { depth = 1 } = item;
        const itemContainerClasses = classNames({
          [classes.itemContainer]: true,
          [classes.checkListItemDepth2]: depth > 1,
        });
        const labelClasses = classNames({
          "text-label": _.isEqual(depth, 2) && hasNestedChildren,
        });

        return !_.isEmpty(item?.childNodes) ? (
          <div key={item.id} className={classes.checkListContainer}>
            <div className={classes.itemContainer} key={item.id}>
              <WithTextHighlight
                text={item.label}
                highlight={searchText}
                className="text-label"
              />
            </div>
            <CheckboxList
              onToggle={onToggle}
              isChecked={isChecked}
              searchText={searchText}
              options={item?.childNodes}
              hasNestedChildren={_hasNestedChildren}
            />
          </div>
        ) : (
          <div className={itemContainerClasses} key={item.id}>
            <WithTextHighlight
              text={item.label}
              highlight={searchText}
              className={labelClasses}
            />
            <Checkbox
              key={item.id}
              dsVersion="2.0"
              isChecked={isChecked(item.id, "leaf")}
              onChange={() => onToggle(item.id, "leaf")}
            />
          </div>
        );
      })}
    </div>
  );
};

const NoSearchResults = () => {
  const { t } = useI18n();

  const noSearchResultsContainer = classNames(
    classes.searchResultsContainer,
    classes.noSearchResultsContainer
  );

  return (
    <div
      className={noSearchResultsContainer}
      style={elevations["elevation-2B"]}
    >
      <EmptyState
        dsVersion="2.0"
        illustration={NoSearchInFilterIllustration}
        title={t("common:no_search_results")}
        subtitle={t("common:no_items_matching_your_search")}
      />
    </div>
  );
};

const ShowSearchResults = ({ data, isChecked, onToggle, searchText }) => {
  if (_.isEmpty(data)) return <NoSearchResults />;

  return (
    <div
      className={classes.searchResultsContainer}
      style={elevations["elevation-2B"]}
    >
      {_.map(data, learningGoal => (
        <Accordian
          isExpanded={true}
          showLeftArrow={true}
          showLabelBorder={true}
          key={learningGoal?.id}
          hideContainerBorder={true}
          title={learningGoal?.label}
          getExpandedContent={() => (
            <div className={classes.accordianContent}>
              <CheckboxList
                isFromSearch={true}
                isChecked={isChecked}
                searchText={searchText}
                onToggle={id => onToggle(id, learningGoal.id)}
                options={learningGoal?.childNodes}
              />
            </div>
          )}
          customHeaderClass={classes.accordianHeader}
          customArrowContainerClass={classes.accordianHeader}
        />
      ))}
    </div>
  );
};

/**
 *
 * @param {*} params
 * @returns Search Input with popover result
 */
const AssessmentLearningGoalsSearchInput = ({
  selectedGoalsValues,
  setSelectedGoalsValues,
  learningGoalsData,
}) => {
  const { t } = useI18n();
  const [searchText, setSearchText] = useState("");

  const searchedData = useMemo(() => {
    const query = _.trim(_.toLower(searchText));
    if (_.isEmpty(query)) return [];
    const searchByQuery = data => getSearchedLearningGoals({ data, query });
    return _.flatMap(learningGoalsData, searchByQuery);
  }, [learningGoalsData, searchText]);

  const isChecked = id =>
    _.includes(_.flatten(_.values(selectedGoalsValues)), id);
  const onToggle = (id, lgId) => {
    if (isChecked(id)) {
      setSelectedGoalsValues(prev =>
        _.mapValues(prev, vals => _.without(vals, id))
      );
    } else {
      setSelectedGoalsValues(prev => ({
        ...prev,
        [lgId]: [...(prev[lgId] || []), id],
      }));
    }
  };

  return (
    <Popover
      isOpen={searchText.length > 0}
      onClickOutside={() => {}}
      align="end"
      positions={["bottom"]}
      containerStyle={{ zIndex: 1500 }}
      content={() => (
        <ShowSearchResults
          data={searchedData}
          onToggle={onToggle}
          isChecked={isChecked}
          searchText={searchText}
        />
      )}
    >
      <div className={classes.searchInput}>
        <SearchInput
          size="medium"
          dsVersion="2.0"
          value={searchText}
          onChange={setSearchText}
          focusSearchBySlash={true}
          placeholder={t("journal:search_learning_goals")}
          testId="assessment-other-learning-goals-search-input"
        />
      </div>
    </Popover>
  );
};

export default AssessmentLearningGoalsSearchInput;
